import { API_BASE } from '@/shared/services/api';
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import MobileScanInput from '@/modules/mobile/components/MobileScanInput';
import SignaturePad from '../components/SignaturePad';
import PhotoCapture from '../components/PhotoCapture';
import {
  Package,
  CheckCircle2,
  MapPin,
  Camera,
  PenTool,
  ScanLine,
  XCircle,
  Navigation,
  User,
  KeyRound,
  CreditCard,
  AlertTriangle,
  Phone,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriver } from '../context/DriverContext';
import { parcelEvents, createPOD, PhotoType } from '@/shared/services/trailService';

const API_URL = API_BASE;

const STEPS = {
  SCAN_PACKAGES: 'scan_packages',
  VERIFICATION: 'verification',
  CAPTURE_POD: 'capture_pod',
  CONFIRM: 'confirm'
};

const StopDeliver = () => {
  // Load ID types from API
  const [idTypes, setIdTypes] = React.useState([]);
  
  React.useEffect(() => {
    const fetchIdTypes = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reference/id-types`);
        if (response.ok) {
          const data = await response.json();
          setIdTypes(data.map(type => ({
            value: type.code,
            label: type.label
          })));
        }
      } catch (error) {
        console.error('Failed to fetch ID types:', error);
        // Fallback
        setIdTypes([
          { value: 'CPF', label: 'CPF' },
          { value: 'RG', label: 'RG' },
          { value: 'OTHER', label: 'Other ID' }
        ]);
      }
    };
    fetchIdTypes();
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRoute, currentStop, completeDelivery, geoLocation, queuePOD } = useDriver();
  
  const [currentStep, setCurrentStep] = useState(STEPS.SCAN_PACKAGES);
  const [scannedPackages, setScannedPackages] = useState([]);
  const [failedPackages, setFailedPackages] = useState([]);
  const [pod, setPod] = useState({
    signature: null,
    photo: null,
    recipientName: '',
    geoStamp: null,
    otpCode: '',
    otpVerified: false,
    idType: '',
    idLast4: '',
    idVerified: false
  });

  // Get stop from context or navigation state
  const stopId = location.state?.stopId;
  const stop = currentStop || activeRoute?.stops?.find(s => s.id === stopId);

  // Get delivery requirements - default to signature + photo
  const requirements = stop?.deliveryRequirements || stop?.delivery_requirements || {
    signature: true,
    photo: true,
    otp: false,
    id_check: false
  };

  const packages = stop?.packages || [];
  const allPackagesAccounted = packages.length === 0 || 
    (scannedPackages.length + failedPackages.length === packages.length);
  
  // Check if all requirements are met - useMemo must be called before any early returns
  const requirementsMet = useMemo(() => {
    const met = {
      signature: !requirements.signature || pod.signature,
      photo: !requirements.photo || pod.photo,
      otp: !requirements.otp || pod.otpVerified,
      id_check: !requirements.id_check || (pod.idType && pod.idLast4?.length === 4)
    };
    return met;
  }, [requirements, pod]);

  const allRequirementsMet = Object.values(requirementsMet).every(Boolean);
  const needsVerification = requirements.otp || requirements.id_check;
  const verificationComplete = (!requirements.otp || pod.otpVerified) && 
    (!requirements.id_check || pod.idVerified);

  // Early return AFTER all hooks
  if (!stop) {
    return (
      <div className="p-4 text-center py-12">
        <XCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Stop Selected</h2>
        <Button onClick={() => navigate('/driver/route-active')}>Back to Route</Button>
      </div>
    );
  }

  const handlePackageScan = (barcode) => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    const pkg = packages.find(p => 
      p.id === barcode || p.package_id === barcode || 
      barcode.includes(p.id) || barcode.includes(p.orderId)
    );

    if (pkg) {
      const pkgId = pkg.id || pkg.package_id;
      if (scannedPackages.some(s => (s.id || s.package_id) === pkgId)) {
        toast.warning('Already Scanned', { description: pkgId });
        return;
      }
      
      if (failedPackages.some(f => f.id === pkgId)) {
        toast.warning('Package Marked as Failed', { description: 'Remove from failed list first' });
        return;
      }
      
      setScannedPackages(prev => [...prev, pkg]);
      
      // Emit trail event for package scanned at stop
      parcelEvents.stopPackageScanned(activeRoute?.id, stop.id, pkgId, driverId);
      
      const remaining = packages.length - scannedPackages.length - failedPackages.length - 1;
      if (remaining === 0) {
        toast.success('All Packages Scanned!', { 
          description: needsVerification ? 'Proceed to verification' : 'Now capture POD' 
        });
        setCurrentStep(needsVerification ? STEPS.VERIFICATION : STEPS.CAPTURE_POD);
      } else {
        toast.success('Package Scanned', { description: `${remaining} remaining` });
      }
    } else {
      toast.error('Wrong Package', { description: 'Not assigned to this stop' });
    }
  };

  const handleMarkPackageFailed = (pkg) => {
    const pkgId = pkg.id || pkg.package_id;
    setFailedPackages(prev => [...prev, { ...pkg, failureReason: 'CUSTOMER_REFUSED' }]);
    setScannedPackages(prev => prev.filter(p => (p.id || p.package_id) !== pkgId));
    toast.info('Package Marked as Failed');
  };

  const handleOTPVerify = () => {
    // In production, this would verify against the sent OTP
    // For now, accept any 6-digit code
    if (pod.otpCode.length >= 4) {
      setPod(prev => ({ ...prev, otpVerified: true }));
      toast.success('OTP Verified!');
      
      // Emit trail event
      const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
      parcelEvents.podOTPVerified(activeRoute?.id, stop.id, stop.shipmentId || stop.id, driverId);
    } else {
      toast.error('Invalid OTP', { description: 'Please enter a valid code' });
    }
  };

  const handleIDVerify = () => {
    if (pod.idType && pod.idLast4?.length === 4) {
      setPod(prev => ({ ...prev, idVerified: true }));
      toast.success('ID Verified!');
      
      // Emit trail event
      const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
      parcelEvents.podIDVerified(
        activeRoute?.id, 
        stop.id, 
        stop.shipmentId || stop.id, 
        pod.idType, 
        pod.idLast4, 
        driverId
      );
    } else {
      toast.error('Invalid ID', { description: 'Please select ID type and enter last 4 digits' });
    }
  };

  const handleSignatureSave = (signatureData, name) => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    setPod(prev => ({
      ...prev,
      signature: signatureData,
      recipientName: name
    }));
    
    // Emit trail event for signature captured
    parcelEvents.podSignatureCaptured(
      activeRoute?.id,
      stop.id,
      stop.shipmentId || stop.id,
      name,
      signatureData,
      driverId
    );
    
    toast.success('Signature Captured', { description: `Signed by ${name}` });
  };

  const handlePhotoCaptured = (photoData) => {
    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    setPod(prev => ({
      ...prev,
      photo: photoData,
      geoStamp: geoLocation
    }));
    
    // Emit trail event for photo captured
    parcelEvents.podPhotoCaptured(
      activeRoute?.id,
      stop.id,
      stop.shipmentId || stop.id,
      photoData,
      'DELIVERY_LOCATION',
      driverId
    );
    
    toast.success('Photo Captured');
  };

  const handleCompleteDelivery = async () => {
    // Final validation - enforce stop closure rules
    if (!allPackagesAccounted) {
      toast.error('Cannot Complete', { 
        description: 'All packages must be scanned or marked as failed' 
      });
      return;
    }

    if (!allRequirementsMet) {
      const missing = [];
      if (!requirementsMet.signature) missing.push('Signature');
      if (!requirementsMet.photo) missing.push('Photo');
      if (!requirementsMet.otp) missing.push('OTP Verification');
      if (!requirementsMet.id_check) missing.push('ID Check');
      
      toast.error('Missing Requirements', { 
        description: `Required: ${missing.join(', ')}` 
      });
      return;
    }

    const driverId = (JSON.parse(localStorage.getItem('ezerpUser') || '{}')?.id || '') || 'driver';
    
    const podRecord = {
      ...pod,
      stopId: stop.id,
      packageIds: scannedPackages.map(p => p.id || p.package_id),
      failedPackageIds: failedPackages.map(p => p.id || p.package_id),
      capturedAt: new Date().toISOString(),
      geoStamp: geoLocation
    };

    // Determine POD level
    let podLevel = 'BASIC';
    if (pod.signature && pod.photo) podLevel = 'SIGNATURE_PHOTO';
    else if (pod.signature) podLevel = 'SIGNATURE';
    else if (pod.photo) podLevel = 'PHOTO';
    if (pod.otpVerified) podLevel = 'OTP';
    if (pod.idVerified) podLevel = 'ID_CHECK';
    if (pod.signature && pod.photo && (pod.otpVerified || pod.idVerified)) podLevel = 'FULL';

    // Emit trail events for stop delivered and POD completed
    await parcelEvents.stopDelivered(
      activeRoute?.id,
      stop.id,
      scannedPackages.map(p => p.id || p.package_id),
      driverId
    );
    
    await parcelEvents.podCompleted(
      activeRoute?.id,
      stop.id,
      stop.shipmentId || stop.id,
      scannedPackages.map(p => p.id || p.package_id),
      podLevel,
      driverId
    );
    
    // Create the formal POD record
    createPOD({
      shipment_id: stop.shipmentId || stop.id,
      stop_id: stop.id,
      route_id: activeRoute?.id,
      package_ids: scannedPackages.map(p => p.id || p.package_id),
      pod_level: podLevel,
      delivery_status: failedPackages.length > 0 ? 'PARTIAL' : 'DELIVERED',
      signature_data: pod.signature,
      recipient_name: pod.recipientName,
      photo_data: pod.photo,
      photo_type: 'DELIVERY_LOCATION',
      otp_verified: pod.otpVerified,
      id_type: pod.idType,
      id_last4: pod.idLast4,
      geo_stamp: geoLocation ? {
        lat: geoLocation.lat,
        lng: geoLocation.lng,
        accuracy: geoLocation.accuracy
      } : null,
      captured_by: driverId
    });

    completeDelivery(stop.id, podRecord);
    queuePOD(podRecord);
    
    const status = failedPackages.length > 0 ? 'Partial Delivery' : 'Delivery Complete';
    toast.success(status, { 
      description: `${scannedPackages.length} delivered, ${failedPackages.length} failed` 
    });
    navigate('/driver/route-active');
  };

  const handleFailDelivery = () => {
    navigate('/driver/stop-failed', { state: { stopId: stop.id } });
  };

  // Determine which step tabs should be shown
  const getStepTabs = () => {
    const tabs = [
      { value: STEPS.SCAN_PACKAGES, icon: ScanLine, label: 'Scan' },
    ];
    
    if (needsVerification) {
      tabs.push({ value: STEPS.VERIFICATION, icon: Shield, label: 'Verify' });
    }
    
    tabs.push(
      { value: STEPS.CAPTURE_POD, icon: Camera, label: 'POD' },
      { value: STEPS.CONFIRM, icon: CheckCircle2, label: 'Complete' }
    );
    
    return tabs;
  };

  const stepTabs = getStepTabs();

  return (
    <div className="p-4 space-y-4" data-testid="stop-deliver">
      {/* Stop Header */}
      <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-white/20">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-lg">{stop.customer}</h2>
              <p className="text-sm opacity-90">{stop.address}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge className="bg-white/20 text-white">
                  <Package className="h-3 w-3 mr-1" />
                  {packages.length} packages
                </Badge>
                <Badge className="bg-white/20 text-white">
                  <ScanLine className="h-3 w-3 mr-1" />
                  {scannedPackages.length} scanned
                </Badge>
                {requirements.otp && (
                  <Badge className="bg-yellow-500/50 text-white">
                    <KeyRound className="h-3 w-3 mr-1" />
                    OTP Required
                  </Badge>
                )}
                {requirements.id_check && (
                  <Badge className="bg-blue-500/50 text-white">
                    <CreditCard className="h-3 w-3 mr-1" />
                    ID Required
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Tabs */}
      <Tabs value={currentStep} onValueChange={setCurrentStep}>
        <TabsList className={`grid w-full grid-cols-${stepTabs.length}`}>
          {stepTabs.map((tab, idx) => (
            <TabsTrigger 
              key={tab.value} 
              value={tab.value} 
              disabled={
                (tab.value === STEPS.VERIFICATION && !allPackagesAccounted) ||
                (tab.value === STEPS.CAPTURE_POD && (!allPackagesAccounted || (needsVerification && !verificationComplete))) ||
                (tab.value === STEPS.CONFIRM && !allRequirementsMet)
              }
              className="gap-1"
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Scan Packages Step */}
        <TabsContent value={STEPS.SCAN_PACKAGES} className="space-y-4 mt-4">
          <MobileScanInput
            title="Scan Packages"
            subtitle="Scan each package at this stop"
            gateId="GATE_STOP_PKG"
            barcodeType="CARTON"
            scanContext="DELIVERY"
            onScanSuccess={handlePackageScan}
            validateScan={async (barcode) => {
              const pkg = packages.find(p => 
                p.id === barcode || p.package_id === barcode || barcode.includes(p.id)
              );
              if (!pkg) return { success: false, result: 'REJECTED', message: 'Package not assigned to this stop' };
              const pkgId = pkg.id || pkg.package_id;
              if (scannedPackages.some(s => (s.id || s.package_id) === pkgId)) {
                return { success: false, result: 'WARNING', message: 'Already scanned' };
              }
              if (failedPackages.some(f => f.id === pkgId)) {
                return { success: false, result: 'WARNING', message: 'Package marked as failed' };
              }
              return { success: true, result: 'OK' };
            }}
          />

          {/* Package List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Packages ({scannedPackages.length}/{packages.length})
                {failedPackages.length > 0 && (
                  <span className="text-red-500 ml-2">({failedPackages.length} failed)</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {packages.map((pkg) => {
                const pkgId = pkg.id || pkg.package_id;
                const isScanned = scannedPackages.some(s => (s.id || s.package_id) === pkgId);
                const isFailed = failedPackages.some(f => f.id === pkgId);
                return (
                  <div key={pkgId} className="px-4 py-2 border-b last:border-0 flex items-center gap-3">
                    {isScanned ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : isFailed ? (
                      <XCircle className="h-4 w-4 text-red-500" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-mono">{pkgId}</p>
                      <p className="text-xs text-muted-foreground">{pkg.weight || pkg.weight_kg}kg</p>
                    </div>
                    {!isScanned && !isFailed && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500"
                        onClick={() => handleMarkPackageFailed(pkg)}
                      >
                        Mark Failed
                      </Button>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {allPackagesAccounted && packages.length > 0 && (
            <Button 
              onClick={() => setCurrentStep(needsVerification ? STEPS.VERIFICATION : STEPS.CAPTURE_POD)}
              className="w-full bg-green-500 hover:bg-green-600"
            >
              {needsVerification ? 'Continue to Verification' : 'Continue to POD'}
            </Button>
          )}
        </TabsContent>

        {/* Verification Step (OTP + ID Check) */}
        {needsVerification && (
          <TabsContent value={STEPS.VERIFICATION} className="space-y-4 mt-4">
            {/* OTP Verification */}
            {requirements.otp && (
              <Card className={pod.otpVerified ? 'border-green-500' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    OTP Verification
                    {pod.otpVerified && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!pod.otpVerified ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Ask customer for the OTP code sent to their registered phone/email.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter OTP code"
                          value={pod.otpCode}
                          onChange={(e) => setPod(prev => ({ ...prev, otpCode: e.target.value }))}
                          maxLength={6}
                          className="font-mono text-lg tracking-widest"
                          data-testid="otp-input"
                        />
                        <Button onClick={handleOTPVerify} disabled={pod.otpCode.length < 4}>
                          Verify
                        </Button>
                      </div>
                      <Button variant="outline" size="sm" className="w-full">
                        <Phone className="h-4 w-4 mr-2" />
                        Resend OTP
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>OTP Verified Successfully</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ID Check */}
            {requirements.id_check && (
              <Card className={pod.idVerified ? 'border-green-500' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    ID Verification
                    {pod.idVerified && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!pod.idVerified ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Verify recipient's ID. Only record the ID type and last 4 digits.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>ID Type</Label>
                          <Select
                            value={pod.idType}
                            onValueChange={(value) => setPod(prev => ({ ...prev, idType: value }))}
                          >
                            <SelectTrigger data-testid="id-type-select">
                              <SelectValue placeholder="Select ID type" />
                            </SelectTrigger>
                            <SelectContent>
                              {idTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Last 4 Digits</Label>
                          <Input
                            type="text"
                            placeholder="XXXX"
                            value={pod.idLast4}
                            onChange={(e) => setPod(prev => ({ 
                              ...prev, 
                              idLast4: e.target.value.replace(/\D/g, '').slice(0, 4) 
                            }))}
                            maxLength={4}
                            className="font-mono text-lg tracking-widest"
                            data-testid="id-last4-input"
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={handleIDVerify} 
                        disabled={!pod.idType || pod.idLast4?.length !== 4}
                        className="w-full"
                      >
                        Verify ID
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span>ID Verified: {pod.idType} ending in {pod.idLast4}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {verificationComplete && (
              <Button 
                onClick={() => setCurrentStep(STEPS.CAPTURE_POD)}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                Continue to POD Capture
              </Button>
            )}
          </TabsContent>
        )}

        {/* Capture POD Step */}
        <TabsContent value={STEPS.CAPTURE_POD} className="space-y-4 mt-4">
          {requirements.signature && (
            <SignaturePad
              recipientName={pod.recipientName}
              onRecipientNameChange={(name) => setPod(prev => ({ ...prev, recipientName: name }))}
              onSave={handleSignatureSave}
              onClear={() => setPod(prev => ({ ...prev, signature: null }))}
            />
          )}

          {requirements.photo && (
            <PhotoCapture
              title="Delivery Photo"
              subtitle="Take a photo showing packages at delivery location"
              onCapture={handlePhotoCaptured}
              onClear={() => setPod(prev => ({ ...prev, photo: null }))}
            />
          )}

          {allRequirementsMet && (
            <Button 
              onClick={() => setCurrentStep(STEPS.CONFIRM)}
              className="w-full bg-green-500 hover:bg-green-600"
            >
              Review & Complete
            </Button>
          )}
        </TabsContent>

        {/* Confirm Step */}
        <TabsContent value={STEPS.CONFIRM} className="space-y-4 mt-4">
          <Card className="border-2 border-green-500">
            <CardContent className="p-4 space-y-4">
              <div className="text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                <h3 className="font-semibold text-lg">Ready to Complete</h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Packages Delivered</span>
                  <span className="font-medium">{scannedPackages.length}</span>
                </div>
                {failedPackages.length > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground text-red-500">Packages Failed</span>
                    <span className="font-medium text-red-500">{failedPackages.length}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Recipient</span>
                  <span className="font-medium flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {pod.recipientName || 'N/A'}
                  </span>
                </div>
                {requirements.signature && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Signature</span>
                    <span className={`font-medium flex items-center gap-1 ${pod.signature ? 'text-green-600' : 'text-red-500'}`}>
                      <PenTool className="h-3 w-3" />
                      {pod.signature ? 'Captured' : 'Missing'}
                    </span>
                  </div>
                )}
                {requirements.photo && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Photo</span>
                    <span className={`font-medium flex items-center gap-1 ${pod.photo ? 'text-green-600' : 'text-red-500'}`}>
                      <Camera className="h-3 w-3" />
                      {pod.photo ? 'Captured' : 'Missing'}
                    </span>
                  </div>
                )}
                {requirements.otp && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">OTP</span>
                    <span className={`font-medium flex items-center gap-1 ${pod.otpVerified ? 'text-green-600' : 'text-red-500'}`}>
                      <KeyRound className="h-3 w-3" />
                      {pod.otpVerified ? 'Verified' : 'Not Verified'}
                    </span>
                  </div>
                )}
                {requirements.id_check && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">ID Check</span>
                    <span className={`font-medium flex items-center gap-1 ${pod.idVerified ? 'text-green-600' : 'text-red-500'}`}>
                      <CreditCard className="h-3 w-3" />
                      {pod.idVerified ? `${pod.idType} ***${pod.idLast4}` : 'Not Verified'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">GPS</span>
                  <span className="font-medium text-green-600 flex items-center gap-1">
                    <Navigation className="h-3 w-3" />
                    {geoLocation ? 'Recorded' : 'Unavailable'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {!allRequirementsMet && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Cannot complete: Missing required items. Please go back and complete all requirements.
              </AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={handleCompleteDelivery}
            className="w-full bg-green-500 hover:bg-green-600"
            size="lg"
            disabled={!allRequirementsMet || !allPackagesAccounted}
            data-testid="complete-delivery-btn"
          >
            <CheckCircle2 className="h-5 w-5 mr-2" />
            {failedPackages.length > 0 ? 'Complete Partial Delivery' : 'Complete Delivery'}
          </Button>
        </TabsContent>
      </Tabs>

      {/* Fail Delivery Button */}
      <Button 
        variant="outline"
        onClick={handleFailDelivery}
        className="w-full text-red-600 border-red-300 hover:bg-red-50"
        data-testid="fail-delivery-btn"
      >
        <XCircle className="h-4 w-4 mr-2" />
        Unable to Deliver (Fail All)
      </Button>
    </div>
  );
};

export default StopDeliver;
